//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { RightsRequestCommunicationType } from '@apis/rights-management/rightsManagementApiModels';
import { Tabs } from '@components/primitives/tabs/Tabs';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import Cuid from '@models/Cuid';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { getIsRightsRequestsViaDmEnabled } from '@uia/rights/selectors/getUIARights';
import { checkIsStatusPendingOrFinalized } from '@uia/rights/utils/statusUtils';
import { isDefined } from '@utils/filterPredicates';

export interface RequestTypeTabsProps {
  children: ReactNode;
  cuid: Cuid;
  requestType: RightsRequestOptions;
  setRequestType: (selectedValue: RightsRequestOptions) => void;
}

export const RequestTypeTabs = ({
  children,
  cuid,
  requestType,
  setRequestType,
}: RequestTypeTabsProps) => {
  const isInstagramDirectMessageEnabled = useSelector(
    getIsRightsRequestsViaDmEnabled,
  );

  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  if (isLoadingMetadata || isErrorMetadata) {
    return null;
  }

  const hasNetworkUsername = isDefined(metadata.socialDetails.networkUsername);
  const status = metadata.rightsRequestStatus.status;
  const isStatusPendingOrFinalized = checkIsStatusPendingOrFinalized(status);

  const {
    rightsRequestStatus: { communicationType },
  } = metadata;

  return (
    <Tabs defaultValue={requestType} value={requestType}>
      {hasNetworkUsername && isInstagramDirectMessageEnabled && (
        <Tabs.List>
          <Trigger
            disabled={
              isStatusPendingOrFinalized
                ? communicationType === RightsRequestCommunicationType.DM
                : false
            }
            onClick={() => setRequestType(RightsRequestOptions.COMMENTS)}
            trackingId={
              UIA_RIGHTS_FLOW_TRACKING_IDS.REQUEST_RIGHTS_IN_COMMENT_BUTTON
            }
            value={RightsRequestOptions.COMMENTS}
          >
            Request in Comments
          </Trigger>
          <Trigger
            disabled={
              isStatusPendingOrFinalized
                ? communicationType !== RightsRequestCommunicationType.DM
                : false
            }
            onClick={() => setRequestType(RightsRequestOptions.DM)}
            trackingId={
              UIA_RIGHTS_FLOW_TRACKING_IDS.REQUEST_RIGHTS_IN_DIRECT_MESSAGE_BUTTON
            }
            value={RightsRequestOptions.DM}
          >
            Request in Direct Message
          </Trigger>
        </Tabs.List>
      )}
      <Content value={RightsRequestOptions.COMMENTS}>{children}</Content>
      <Content value={RightsRequestOptions.DM}>{children}</Content>
    </Tabs>
  );
};

const Trigger = styled(Tabs.Trigger)(
  ({ theme }) => `
  display: grid;
  place-items: center;
  padding: ${theme.spacings[3]} ${theme.spacings[0]};
  `,
);

const Content = styled(Tabs.Content)(
  ({ theme }) => `
    background-color: ${theme.colors.gray00};
  `,
);
